import { useMemo } from "react";
import { cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { PortalNavbar } from "./PortalNavbar";
import { PortalSidebar } from "./PortalSidebar";
import { BlockRenderer, PopupBlock } from "./blocks";
import { GRID_CONTAINER_CLASSES, findPageContainingBlockType } from "../../shared/schema";
import type { Block, ChildPage, Page, PortalSchema, PopupBlock as PopupBlockType } from "../../shared/schema";
import { useCartItemCount } from "@tellescope/shop";
import { SHOP_STEP_QUERY_PARAM } from "./blocks/ShopBlock";

interface PortalContentProps {
  schema: PortalSchema;
  /** Pages to show in navigation (pre-filtered by visibility by the caller) */
  visiblePages: Page[];
  currentPage: Page | ChildPage | null;
  onNavigate: (pageId: string, queryParams?: Record<string, string>) => void;
  onLogout?: () => void;
  logoURL?: string;
  organizationName?: string;
  /**
   * When true, shows sidebar navigation on larger screens.
   * When false, always shows the hamburger menu navbar.
   */
  showSidebar?: boolean;
  /**
   * When true, renders navigation items as <Link> components for native link behavior.
   * When false, renders as buttons that call onNavigate callback.
   */
  useLinks?: boolean;
  /** Empty state message when no blocks are on the page */
  emptyMessage?: string;
}

/**
 * Shared portal content renderer used by both the enduser portal and admin preview.
 * Handles conditional rendering of sidebar vs navbar based on settings and screen size.
 */
export const PortalContent: React.FC<PortalContentProps> = ({
  schema,
  visiblePages,
  currentPage,
  onNavigate,
  onLogout,
  logoURL,
  organizationName,
  showSidebar = false,
  useLinks = false,
  emptyMessage,
}) => {
  const { t } = useTranslation();
  const cartItemCount = useCartItemCount();

  const defaultEmptyMessage = t("No content available on this page.");

  // The shop block is a full standalone experience (matching the prototype's own dedicated,
  // uncapped-width catalog page), not page content meant to share a narrow reading-width column
  // with other blocks — so a page whose only block is "shop" skips the max-w-4xl grid wrapper
  // every other block type renders inside. A shop block alongside other content (or nested in a
  // section) still gets the standard container, since it's no longer the page's sole focus.
  const isSoleShopBlockPage = currentPage?.blocks.length === 1 && currentPage.blocks[0]?.type === "shop";

  // The cart icon is global chrome, visible from any page, but only when a shop block exists
  // somewhere in the org's portal schema at all — most orgs won't have one.
  const shopPage = useMemo(() => findPageContainingBlockType(schema, "shop"), [schema]);
  const handleCartClick = shopPage ? () => onNavigate(shopPage.pageId, { [SHOP_STEP_QUERY_PARAM]: "cart" }) : undefined;

  // Collect global popups from all pages, including sub-pages (popups with showOnAllPages: true)
  const globalPopups = useMemo(() => {
    const popups: PopupBlockType[] = [];
    const collectFromBlocks = (blocks: Block[]) => {
      for (const block of blocks) {
        if (block.type === "popup" && block.props.showOnAllPages) {
          popups.push(block);
        }
        // Also check inside sections
        if (block.type === "section") {
          for (const childBlock of block.blocks) {
            if (childBlock.type === "popup" && childBlock.props.showOnAllPages) {
              popups.push(childBlock);
            }
          }
        }
      }
    };
    for (const page of schema.pages) {
      collectFromBlocks(page.blocks);
      for (const childPage of page.pages ?? []) {
        collectFromBlocks(childPage.blocks);
      }
    }
    return popups;
  }, [schema.pages]);

  return (
    <div className="h-[100dvh] bg-background flex">
      {/* Global popups - render outside page content since they're modals */}
      {globalPopups.map((popup) => (
        <PopupBlock key={popup.id} block={popup} isGlobalRender />
      ))}
      {/* Sidebar - only when enabled */}
      {showSidebar && (
        <PortalSidebar
          pages={visiblePages}
          rootSlug={schema.pages[0]?.slug}
          currentPage={currentPage}
          onNavigate={onNavigate}
          portalName={schema.name}
          onLogout={onLogout}
          logoURL={logoURL}
          organizationName={organizationName}
          useLinks={useLinks}
          cartItemCount={cartItemCount}
          onCartClick={handleCartClick}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-full min-w-0">
        {/* Navbar - hidden when sidebar is shown */}
        {!showSidebar && (
          <PortalNavbar
            pages={visiblePages}
            rootSlug={schema.pages[0]?.slug}
            currentPage={currentPage}
            onNavigate={onNavigate}
            portalName={schema.name}
            onLogout={onLogout}
            logoURL={logoURL}
            organizationName={organizationName}
            useLinks={useLinks}
            cartItemCount={cartItemCount}
            onCartClick={handleCartClick}
          />
        )}

        {/* Portal content - renders current page blocks in a responsive grid */}
        {isSoleShopBlockPage ? (
          <div className="flex-1 overflow-auto">
            {currentPage?.blocks.map((block) => <BlockRenderer key={block.id} block={block} />)}
          </div>
        ) : (
          <div className="flex-1 overflow-auto p-6">
            <div className={cn("max-w-4xl mx-auto", GRID_CONTAINER_CLASSES)}>
              {currentPage?.blocks.map((block) => <BlockRenderer key={block.id} block={block} />)}
              {(!currentPage || currentPage.blocks.length === 0) && (
                <div className="col-span-12 text-center py-12 text-muted-foreground">
                  <p>{emptyMessage ?? defaultEmptyMessage}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
